import { useSession } from '@side/next-auth/react';
import { useMemo } from 'react';
import type { MlsVendorOption } from 'src/components/MlsVendorAutocomplete';
import { useIdentity } from '$api-clients/hooks';

/**
 * The MLSs available to the signed-in user, from identity's catalog: every
 * MLS in the markets the user (and their submit-as partners) can transact in.
 * Entries without a Home Junction market code are excluded — they cannot be
 * searched.
 */
export const useAvailableMls = () => {
  const userId = useSession().data?.user?.id;
  const { data, isLoading, error } = useIdentity(
    '/users/{userId}/multiple-listing-services/available',
    userId ? { params: { path: { userId } } } : null,
  );

  const mlsVendorOptions: MlsVendorOption[] = useMemo(
    () =>
      (data?.data ?? [])
        .map((mls) => ({
          id: mls.id,
          name: mls.name,
          code: mls.code,
          // providerId is null for catalog entries with no provider mapping;
          // those can't be searched through the provider, so the filter below
          // drops them from the picker.
          providerId: mls.providerId?.trim() ?? '',
          listingIdTip: mls.listingIdTip ?? null,
          locales: mls.locales,
        }))
        .filter((option) => option.providerId !== '')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data],
  );

  return { mlsVendorOptions, loading: isLoading, error };
};
